import React, { useCallback, useEffect, useId, useRef, useState } from "react";

// Roles that count as focusable menu items for arrow-key navigation.
const MENU_ITEM_SELECTOR =
	'[role="menuitem"],[role="menuitemradio"],[role="menuitemcheckbox"]';

/**
 * Lightweight popover menu: a trigger button plus an anchored panel. The
 * primitive itself closes on outside-click and Escape; closing after a
 * selection is the consumer's responsibility via the `close` callback passed to
 * `children`. Rendering of the panel contents is delegated to `children`.
 *
 * Accessibility (RFC-048): focus is moved into the panel on open (first menu
 * item) and returned to the trigger on close. The panel implements the WAI-ARIA
 * menu keyboard model — ArrowUp / ArrowDown / Home / End move focus between the
 * `role="menuitem*"` children it renders.
 */
export const DropdownMenu = ({
	trigger,
	children,
	align = "right",
	panelClassName = "",
	buttonClassName = "",
	ariaLabel,
	title,
}) => {
	const [open, setOpen] = useState(false);
	const containerRef = useRef(null);
	const panelRef = useRef(null);
	const triggerRef = useRef(null);
	// When set, focus should return to the trigger after the panel unmounts
	// (Escape, outside-click, or selection). Distinguishes a real close from the
	// initial closed render so we don't steal focus on mount.
	const restoreFocusRef = useRef(false);
	const menuId = useId();

	const close = useCallback(() => {
		restoreFocusRef.current = true;
		setOpen(false);
	}, []);

	const getItems = useCallback(
		() =>
			Array.from(
				panelRef.current?.querySelectorAll(MENU_ITEM_SELECTOR) || [],
			).filter((el) => !el.hasAttribute("disabled")),
		[],
	);

	// Outside-click and Escape close the menu (and restore focus to the trigger).
	useEffect(() => {
		if (!open) return undefined;

		const handlePointerDown = (event) => {
			if (!containerRef.current?.contains(event.target)) {
				close();
			}
		};
		const handleKeyDown = (event) => {
			if (event.key === "Escape") close();
		};

		document.addEventListener("mousedown", handlePointerDown);
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("mousedown", handlePointerDown);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [open, close]);

	// Move focus into the panel on open; return it to the trigger on close.
	useEffect(() => {
		if (open) {
			const items = getItems();
			(items[0] || panelRef.current)?.focus();
		} else if (restoreFocusRef.current) {
			restoreFocusRef.current = false;
			triggerRef.current?.focus();
		}
	}, [open, getItems]);

	// WAI-ARIA menu keyboard navigation between items.
	const handlePanelKeyDown = (event) => {
		const items = getItems();
		if (items.length === 0) return;
		const currentIndex = items.indexOf(document.activeElement);

		switch (event.key) {
			case "ArrowDown": {
				event.preventDefault();
				const next = items[(currentIndex + 1 + items.length) % items.length];
				next?.focus();
				break;
			}
			case "ArrowUp": {
				event.preventDefault();
				const prev = items[(currentIndex - 1 + items.length) % items.length];
				prev?.focus();
				break;
			}
			case "Home": {
				event.preventDefault();
				items[0]?.focus();
				break;
			}
			case "End": {
				event.preventDefault();
				items[items.length - 1]?.focus();
				break;
			}
			default:
				break;
		}
	};

	return (
		<div ref={containerRef} className="relative">
			<button
				ref={triggerRef}
				type="button"
				onClick={() => setOpen((prev) => !prev)}
				aria-haspopup="menu"
				aria-expanded={open}
				aria-controls={menuId}
				aria-label={ariaLabel}
				title={title}
				className={buttonClassName}
			>
				{trigger}
			</button>
			{open && (
				<div
					ref={panelRef}
					id={menuId}
					role="menu"
					aria-label={ariaLabel}
					tabIndex={-1}
					onKeyDown={handlePanelKeyDown}
					className={`absolute z-30 mt-2 origin-top rounded-xl bg-[var(--surface-elevated)] p-1.5 shadow-2xl ring-1 ring-[var(--surface-elevated-ring)] animate-in fade-in zoom-in-95 duration-150 ${
						align === "right" ? "right-0" : "left-0"
					} ${panelClassName}`}
				>
					{typeof children === "function" ? children({ close }) : children}
				</div>
			)}
		</div>
	);
};
